import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUpdateEffect } from 'react-use';
import { FormControl } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';

import type { LegalStatus } from '@/api/domains/contractors.types';
import { RoundedButton } from '@/components/buttons';
import { CustomFormLabel, RHFIntegerTextField, RHFTextField } from '@/components/form-controls';
import type { SignUpCompany } from '@/pages/auth/sign-up/store/signUp.store';
import { isSafeNumeric } from '@/shared/helpers/strings.helpers.ts';

import { type FormValues } from '..';

// ----------------------------------------------------------------------

export type CompanyFormFields = {
  legalStatus: LegalStatus | null;
  companyName: string | null;
  tin: number | null;
};

export const getDefaultCompanyFormValues = (shouldPrefillForm: boolean, storedCompanyData: Partial<SignUpCompany>) => ({
  legalStatus: shouldPrefillForm ? storedCompanyData.legalStatus : null,
  companyName: shouldPrefillForm ? storedCompanyData.companyName : '',
  tin: shouldPrefillForm ? storedCompanyData.tin : null,
});

// ----------------------------------------------------------------------

type CompanyInfoFormProps = {
  handleLoadData: () => Promise<void>;
  isLoading: boolean;
};

const TIN_MAX_LENGTH = 9;

export const CompanyInfoForm = (props: CompanyInfoFormProps) => {
  const {
    watch,
    formState: { dirtyFields, errors },
    resetField,
  } = useFormContext<FormValues>();
  const { handleLoadData, isLoading } = props;
  const { t } = useTranslation('auth-sign-up');

  const tin = watch('tin');
  const legalStatus = watch('legalStatus');

  useUpdateEffect(() => {
    if (dirtyFields.tin) {
      resetField('companyName');
      resetField('legalStatus');
    }
  }, [tin]);

  const hasTinError = errors?.tin || false;

  return (
    <Grid container rowSpacing={3} columnSpacing={2}>
      <Grid container mobile={12} tablet={12} alignItems={hasTinError ? 'center' : 'flex-end'}>
        <Grid mobile={12} tablet={6}>
          {/* TIN */}
          <FormControl fullWidth>
            <CustomFormLabel isRequired htmlFor="tin">
              {t('forms.company-form.labels.tin')}
            </CustomFormLabel>
            <RHFIntegerTextField
              isAllowed={({ value }) => isSafeNumeric(value) && value.length <= TIN_MAX_LENGTH}
              fullWidth
              name="tin"
              id="tin"
              variant="filled"
            />
          </FormControl>
          {/* TIN END */}
        </Grid>
        <Grid mobile={6} tablet={6}>
          <RoundedButton disabled={!tin} loading={isLoading} onClick={handleLoadData} variant="outlined">
            {t('forms.company-form.actions.load-data')}
          </RoundedButton>
        </Grid>
      </Grid>
      <Grid mobile={12} tablet={6}>
        {/* LEGAL STATUS */}
        <FormControl fullWidth>
          <CustomFormLabel isRequired htmlFor="legalStatus">
            {t('forms.company-form.labels.legal-status')}
          </CustomFormLabel>

          <RHFTextField
            InputProps={{ value: legalStatus?.name || '' }}
            isClearable={false}
            fullWidth
            name="legalStatus"
            id="legalStatus"
            variant="filled"
            disabled
          />
        </FormControl>
        {/* LEGAL STATUS END */}
      </Grid>

      <Grid mobile={12} tablet={6}>
        {/* NAME */}
        <FormControl fullWidth>
          <CustomFormLabel isRequired htmlFor="companyName">
            {t('forms.company-form.labels.company-name')}
          </CustomFormLabel>
          <RHFTextField isClearable={false} fullWidth name="companyName" id="companyName" variant="filled" disabled />
        </FormControl>
        {/* NAME END */}
      </Grid>
    </Grid>
  );
};
